import { useEffect, useState, useMemo, type CSSProperties } from "react";
import {
  getCatalogChangeStats,
  getCatalogChanges,
  triggerCatalogSync,
  getDeletedItemsReview,
  resolveDeletedItems,
  type CatalogChangeStats,
  type CatalogChangeRow,
  type DeletedItemRow,
} from "../../lib/api";
import WhiskeyStudioTabs from "../../components/admin/WhiskeyStudioTabs";

export default function AdminCatalogMonitor() {
  const [tab, setTab] = useState<"sync" | "deleted">("sync");

  // Sync Feed State
  const [windowDays, setWindowDays] = useState<number>(30);
  const [changeType, setChangeType] = useState<string>("");
  const [fieldFilter, setFieldFilter] = useState<string>("");
  const [stats, setStats] = useState<CatalogChangeStats | null>(null);
  const [changes, setChanges] = useState<CatalogChangeRow[]>([]);
  const [loadingSync, setLoadingSync] = useState(true);
  const [syncingNow, setSyncingNow] = useState(false);

  // Deleted Items State
  const [deletedItems, setDeletedItems] = useState<DeletedItemRow[]>([]);
  const [deletedAuditDate, setDeletedAuditDate] = useState<string | null>(null);
  const [deletedStatusFilter, setDeletedStatusFilter] = useState<string>("all");
  const [deletedSearch, setDeletedSearch] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [resolvingBulk, setResolvingBulk] = useState(false);

  // Messages
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 4000);
  };

  // Load Sync Monitor Data
  const loadSyncData = async () => {
    setLoadingSync(true);
    setErrorMsg(null);
    try {
      const [sRes, cRes] = await Promise.all([
        getCatalogChangeStats(windowDays),
        getCatalogChanges({
          days: windowDays,
          change_type: changeType || undefined,
          field: fieldFilter || undefined,
        }),
      ]);
      setStats(sRes);
      setChanges(cRes || []);
    } catch (err: any) {
      setErrorMsg("Failed to load catalog sync stats: " + (err?.message || "Error"));
    } finally {
      setLoadingSync(false);
    }
  };

  // Load Deleted Items Data
  const loadDeletedData = async () => {
    setLoadingDeleted(true);
    setErrorMsg(null);
    try {
      const res = await getDeletedItemsReview();
      setDeletedItems(res.items || []);
      setDeletedAuditDate(res.audit_date || null);
      setSelectedIds(new Set());
    } catch (err: any) {
      setErrorMsg("Failed to load deleted items queue: " + (err?.message || "Error"));
    } finally {
      setLoadingDeleted(false);
    }
  };

  useEffect(() => {
    if (tab === "sync") loadSyncData();
    else loadDeletedData();
  }, [tab, windowDays, changeType, fieldFilter]);

  // Run Sync Now Trigger
  const handleRunSync = async () => {
    setSyncingNow(true);
    try {
      await triggerCatalogSync();
      showToast("Catalog sync initiated. Refreshing feed...");
      setTimeout(() => {
        loadSyncData();
        setSyncingNow(false);
      }, 3000);
    } catch (err: any) {
      alert("Sync failed: " + (err?.message || "Error"));
      setSyncingNow(false);
    }
  };

  // Bulk Deleted Items Actions
  const filteredDeletedItems = useMemo(() => {
    return deletedItems.filter((item) => {
      if (deletedStatusFilter === "active" && !item.is_active) return false;
      if (deletedStatusFilter === "inactive" && item.is_active) return false;
      if (deletedStatusFilter === "reviewed" && !item.is_reviewed) return false;
      if (deletedSearch.trim()) {
        const q = deletedSearch.toLowerCase();
        return (
          item.name.toLowerCase().includes(q) ||
          item.square_id.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [deletedItems, deletedStatusFilter, deletedSearch]);

  const handleToggleSelectAll = () => {
    if (selectedIds.size === filteredDeletedItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredDeletedItems.map((i) => i.square_id)));
    }
  };

  const handleToggleSelect = (sqId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sqId)) next.delete(sqId);
      else next.add(sqId);
      return next;
    });
  };

  const handleBulkAction = async (action: "deactivate" | "keep") => {
    const selected = deletedItems.filter((i) => selectedIds.has(i.square_id));
    if (selected.length === 0) {
      alert("Please select at least one item.");
      return;
    }
    if (
      action === "deactivate" &&
      !window.confirm(
        `Deactivate ${selected.length} selected item(s)? They will be hidden from menus.`
      )
    ) {
      return;
    }

    setResolvingBulk(true);
    try {
      await resolveDeletedItems(selected, action);
      showToast(
        `${action === "deactivate" ? "Deactivated" : "Kept"} ${selected.length} items.`
      );
      await loadDeletedData();
    } catch (err: any) {
      alert("Action failed: " + (err?.message || "Error"));
    } finally {
      setResolvingBulk(false);
    }
  };

  const fmtValue = (field: string | null, v: any) => {
    if (v === null || v === undefined) return <span style={{ color: "var(--muted)" }}>—</span>;
    if (field === "price_cents" || field === "cost_cents") {
      return `$${(Number(v) / 100).toFixed(2)}`;
    }
    if (Array.isArray(v)) return v.join(", ");
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };

  const fmtTimeAgo = (iso?: string | null) => {
    if (!iso) return "—";
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  };

  return (
    <div style={styles.container}>
      <WhiskeyStudioTabs activeTab="drift" badgeCounts={{ drift: changes.length }} />
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Catalog Health & Drift Monitor</h1>
          <p style={styles.subtitle}>
            Square catalog mirror change logs, drift alerts, and deleted items review
          </p>
        </div>
        <div style={styles.tabToggle}>
          <button
            style={{
              ...styles.tabBtn,
              ...(tab === "sync" ? styles.tabBtnActive : {}),
            }}
            onClick={() => setTab("sync")}
          >
            Sync Changes & Drift
          </button>
          <button
            style={{
              ...styles.tabBtn,
              ...(tab === "deleted" ? styles.tabBtnActive : {}),
            }}
            onClick={() => setTab("deleted")}
          >
            Deleted Items Review
          </button>
        </div>
      </div>

      {/* Messages */}
      {errorMsg && <div style={styles.errorBanner}>{errorMsg}</div>}
      {successMsg && <div style={styles.successBanner}>{successMsg}</div>}

      {/* ═══ TAB 1: SYNC CHANGES & DRIFT ═══ */}
      {tab === "sync" && (
        <div>
          {/* Controls Bar */}
          <div style={styles.controlsBar}>
            <div style={styles.filterGroup}>
              <label style={styles.ctrlLabel}>Window</label>
              <select
                style={styles.select}
                value={windowDays}
                onChange={(e) => setWindowDays(parseInt(e.target.value, 10) || 30)}
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            </div>

            <div style={styles.segGroup}>
              <button
                style={{
                  ...styles.segBtn,
                  ...(!changeType ? styles.segBtnActive : {}),
                }}
                onClick={() => setChangeType("")}
              >
                All
              </button>
              <button
                style={{
                  ...styles.segBtn,
                  ...(changeType === "created" ? styles.segBtnActive : {}),
                }}
                onClick={() => setChangeType("created")}
              >
                Created
              </button>
              <button
                style={{
                  ...styles.segBtn,
                  ...(changeType === "updated" ? styles.segBtnActive : {}),
                }}
                onClick={() => setChangeType("updated")}
              >
                Updated
              </button>
              <button
                style={{
                  ...styles.segBtn,
                  ...(changeType === "deactivated" ? styles.segBtnActive : {}),
                }}
                onClick={() => setChangeType("deactivated")}
              >
                Deactivated
              </button>
            </div>

            {stats?.by_field && stats.by_field.length > 0 && (
              <div style={styles.filterGroup}>
                <label style={styles.ctrlLabel}>Field</label>
                <select
                  style={styles.select}
                  value={fieldFilter}
                  onChange={(e) => setFieldFilter(e.target.value)}
                >
                  <option value="">All fields</option>
                  {stats.by_field.map((f) => (
                    <option key={f.field_name} value={f.field_name}>
                      {f.field_name} ({f.change_count})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <button
              style={styles.runSyncBtn}
              disabled={syncingNow}
              onClick={handleRunSync}
            >
              {syncingNow ? "Syncing..." : "⟳ Run Sync Now"}
            </button>
            <button
              style={styles.refreshBtn}
              onClick={loadSyncData}
              disabled={loadingSync}
            >
              {loadingSync ? "..." : "↻"}
            </button>
          </div>

          {/* Sync Stats Cards */}
          {stats && (
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={styles.statVal}>
                  {fmtTimeAgo(stats.last_run?.completed_at || stats.last_run?.started_at)}
                </div>
                <div style={styles.statLabel}>Last Sync Run</div>
                <div style={styles.statSub}>
                  +{stats.last_run?.items_created || 0} created · ~
                  {stats.last_run?.items_updated || 0} updated
                </div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statVal}>{stats.totals?.total || 0}</div>
                <div style={styles.statLabel}>Changes ({windowDays}d)</div>
                <div style={styles.statSub}>Field-level modifications</div>
              </div>
              <div style={styles.statCard}>
                <div
                  style={{
                    ...styles.statVal,
                    color: stats.price_changes ? "var(--gold, #F9AF36)" : "var(--text)",
                  }}
                >
                  {stats.price_changes || 0}
                </div>
                <div style={styles.statLabel}>Price / Cost Diffs</div>
                <div style={styles.statSub}>Price & cost updates</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statVal}>{stats.totals?.deactivated || 0}</div>
                <div style={styles.statLabel}>Deactivations</div>
                <div style={styles.statSub}>Removed from Square</div>
              </div>
            </div>
          )}

          {/* Recent Changes Table */}
          <div style={styles.tableCard}>
            <div style={styles.tableHeadRow}>
              <div style={styles.sectionHeading}>
                Recent Catalog Changes ({changes.length})
              </div>
            </div>
            {loadingSync ? (
              <div style={styles.empty}>Loading change feed...</div>
            ) : changes.length === 0 ? (
              <div style={styles.empty}>No changes logged in this window.</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>When</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Item</th>
                    <th style={styles.th}>Field</th>
                    <th style={styles.th}>Diff / Value</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.map((c) => (
                    <tr key={c.id} style={styles.tr}>
                      <td style={styles.tdDate}>{fmtTimeAgo(c.recorded_at)}</td>
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.typeBadge,
                            ...(c.change_type === "created"
                              ? styles.badgeCreated
                              : c.change_type === "deactivated"
                              ? styles.badgeDeactivated
                              : styles.badgeUpdated),
                          }}
                        >
                          {c.change_type}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <strong>{c.item_name || "Unknown Item"}</strong>
                        {c.category_slug && (
                          <div style={styles.subText}>{c.category_slug}</div>
                        )}
                      </td>
                      <td style={styles.td}>{c.field_name || "—"}</td>
                      <td style={styles.tdDiff}>
                        {c.change_type === "updated" ? (
                          <>
                            <span style={styles.oldVal}>
                              {fmtValue(c.field_name, c.old_value)}
                            </span>
                            <span style={{ margin: "0 6px", color: "var(--muted)" }}>→</span>
                            <span style={styles.newVal}>
                              {fmtValue(c.field_name, c.new_value)}
                            </span>
                          </>
                        ) : (
                          fmtValue(c.field_name, c.new_value)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ═══ TAB 2: DELETED ITEMS REVIEW ═══ */}
      {tab === "deleted" && (
        <div>
          {/* Deleted Items Toolbar */}
          <div style={styles.deletedToolbar}>
            <input
              type="text"
              style={styles.searchInput}
              placeholder="Search deleted items..."
              value={deletedSearch}
              onChange={(e) => setDeletedSearch(e.target.value)}
            />
            <select
              style={styles.select}
              value={deletedStatusFilter}
              onChange={(e) => setDeletedStatusFilter(e.target.value)}
            >
              <option value="all">All Items</option>
              <option value="active">Active in DB</option>
              <option value="inactive">Already Inactive</option>
              <option value="reviewed">Marked Reviewed</option>
            </select>

            <div style={styles.bulkActions}>
              <label style={{ fontSize: 13, color: "var(--text)", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="checkbox"
                  checked={
                    filteredDeletedItems.length > 0 &&
                    selectedIds.size === filteredDeletedItems.length
                  }
                  onChange={handleToggleSelectAll}
                />
                Select All ({selectedIds.size})
              </label>
              <button
                style={styles.dangerBtn}
                disabled={selectedIds.size === 0 || resolvingBulk}
                onClick={() => handleBulkAction("deactivate")}
              >
                Deactivate Selected
              </button>
              <button
                style={styles.keepBtn}
                disabled={selectedIds.size === 0 || resolvingBulk}
                onClick={() => handleBulkAction("keep")}
              >
                Keep Selected
              </button>
            </div>
          </div>

          {/* Deleted Items Table */}
          <div style={styles.tableCard}>
            <div style={styles.tableHeadRow}>
              <div style={styles.sectionHeading}>
                Blocked Deactivations Queue ({filteredDeletedItems.length})
              </div>
              {deletedAuditDate && (
                <span style={styles.auditDate}>
                  Flagged on {new Date(deletedAuditDate).toLocaleDateString()}
                </span>
              )}
            </div>

            {loadingDeleted ? (
              <div style={styles.empty}>Loading deleted items...</div>
            ) : filteredDeletedItems.length === 0 ? (
              <div style={styles.empty}>No deleted items pending review.</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, width: 40 }}></th>
                    <th style={styles.th}>Item Name</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Square ID</th>
                    <th style={styles.th}>DB Status</th>
                    <th style={styles.th}>Review</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDeletedItems.map((item) => {
                    const isSelected = selectedIds.has(item.square_id);
                    return (
                      <tr key={item.square_id} style={styles.tr}>
                        <td style={styles.td}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelect(item.square_id)}
                          />
                        </td>
                        <td style={styles.td}>
                          <strong>{item.name}</strong>
                        </td>
                        <td style={styles.td}>
                          <span style={styles.typeTag}>{item.ref_type}</span>
                        </td>
                        <td style={styles.tdMono}>{item.square_id}</td>
                        <td style={styles.td}>
                          <span
                            style={{
                              ...styles.statusTag,
                              ...(item.is_active
                                ? styles.tagActive
                                : styles.tagInactive),
                            }}
                          >
                            {item.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td style={styles.td}>
                          {item.is_reviewed ? (
                            <span style={styles.reviewedTag}>Reviewed</span>
                          ) : (
                            <span style={styles.pendingTag}>Pending</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "24px 20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 24,
    color: "var(--gold, #F9AF36)",
    margin: "0 0 4px 0",
  },
  subtitle: {
    fontSize: 13,
    color: "var(--muted, #8A7A66)",
    margin: 0,
  },
  tabToggle: {
    display: "flex",
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    padding: 3,
  },
  tabBtn: {
    background: "transparent",
    border: "none",
    color: "var(--muted, #8A7A66)",
    padding: "6px 14px",
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  tabBtnActive: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
  },
  errorBanner: {
    background: "rgba(224, 80, 64, 0.15)",
    border: "1px solid rgba(224, 80, 64, 0.4)",
    color: "#ff8b7b",
    padding: "10px 14px",
    borderRadius: 6,
    marginBottom: 16,
    fontSize: 13,
  },
  successBanner: {
    background: "rgba(39, 174, 96, 0.15)",
    border: "1px solid rgba(39, 174, 96, 0.4)",
    color: "#4ade80",
    padding: "10px 14px",
    borderRadius: 6,
    marginBottom: 16,
    fontSize: 13,
  },
  controlsBar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: "10px 16px",
    marginBottom: 20,
    flexWrap: "wrap",
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  ctrlLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    color: "var(--muted, #8A7A66)",
  },
  select: {
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 4,
    padding: "5px 8px",
    fontSize: 12,
    color: "var(--text, #F0E6D4)",
  },
  segGroup: {
    display: "flex",
    background: "rgba(0,0,0,0.3)",
    borderRadius: 4,
    border: "1px solid var(--border, #2A1F14)",
  },
  segBtn: {
    background: "transparent",
    border: "none",
    color: "var(--muted, #8A7A66)",
    padding: "5px 10px",
    fontSize: 12,
    cursor: "pointer",
  },
  segBtnActive: {
    background: "rgba(249, 175, 54, 0.15)",
    color: "var(--gold, #F9AF36)",
    fontWeight: 700,
  },
  runSyncBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
    border: "none",
    borderRadius: 5,
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    marginLeft: "auto",
  },
  refreshBtn: {
    background: "transparent",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--text, #F0E6D4)",
    borderRadius: 4,
    padding: "5px 10px",
    fontSize: 12,
    cursor: "pointer",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 12,
    marginBottom: 20,
  },
  statCard: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: "14px 16px",
  },
  statVal: {
    fontFamily: "Georgia, serif",
    fontSize: 20,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
  },
  statLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "var(--muted, #8A7A66)",
    marginTop: 2,
  },
  statSub: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    marginTop: 4,
  },
  tableCard: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    // auto, not hidden: on phones the columns past the edge were clipped and unreachable.
    overflowX: "auto",
  },
  tableHeadRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    borderBottom: "1px solid var(--border, #2A1F14)",
  },
  sectionHeading: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    background: "rgba(0,0,0,0.3)",
    textAlign: "left",
    padding: "10px 14px",
    color: "var(--gold, #F9AF36)",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: "1px solid var(--border, #2A1F14)",
  },
  tr: {
    borderBottom: "1px solid rgba(255,255,255,0.03)",
  },
  td: {
    padding: "10px 14px",
    color: "var(--text, #F0E6D4)",
  },
  tdDate: {
    padding: "10px 14px",
    color: "var(--muted, #8A7A66)",
    fontSize: 12,
    whiteSpace: "nowrap",
  },
  tdDiff: {
    padding: "10px 14px",
    color: "var(--text, #F0E6D4)",
    fontSize: 12,
  },
  tdMono: {
    padding: "10px 14px",
    color: "var(--muted, #8A7A66)",
    fontFamily: "monospace",
    fontSize: 11,
  },
  subText: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    marginTop: 2,
  },
  oldVal: {
    color: "#ff8b7b",
    textDecoration: "line-through",
  },
  newVal: {
    color: "#4ade80",
    fontWeight: 600,
  },
  typeBadge: {
    padding: "2px 6px",
    borderRadius: 3,
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
  },
  badgeCreated: {
    background: "rgba(39, 174, 96, 0.15)",
    color: "#4ade80",
  },
  badgeUpdated: {
    background: "rgba(249, 175, 54, 0.15)",
    color: "var(--gold, #F9AF36)",
  },
  badgeDeactivated: {
    background: "rgba(224, 80, 64, 0.15)",
    color: "#ff8b7b",
  },
  deletedToolbar: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: 20,
    flexWrap: "wrap",
  },
  searchInput: {
    flex: 1,
    minWidth: 200,
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 4,
    padding: "6px 10px",
    fontSize: 12,
    color: "var(--text, #F0E6D4)",
  },
  bulkActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  dangerBtn: {
    background: "transparent",
    border: "1px solid #E05040",
    color: "#ff8b7b",
    borderRadius: 4,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  keepBtn: {
    background: "transparent",
    border: "1px solid #27ae60",
    color: "#4ade80",
    borderRadius: 4,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  typeTag: {
    fontSize: 10,
    background: "rgba(255,255,255,0.05)",
    color: "var(--text, #F0E6D4)",
    padding: "2px 6px",
    borderRadius: 3,
    textTransform: "uppercase",
  },
  statusTag: {
    fontSize: 10,
    padding: "2px 6px",
    borderRadius: 3,
    fontWeight: 700,
    textTransform: "uppercase",
  },
  tagActive: {
    background: "rgba(39, 174, 96, 0.15)",
    color: "#4ade80",
  },
  tagInactive: {
    background: "rgba(224, 80, 64, 0.15)",
    color: "#ff8b7b",
  },
  reviewedTag: {
    fontSize: 10,
    color: "var(--gold, #F9AF36)",
    fontWeight: 700,
  },
  pendingTag: {
    fontSize: 10,
    color: "var(--muted, #8A7A66)",
  },
  auditDate: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
  },
  empty: {
    textAlign: "center",
    padding: 40,
    color: "var(--muted, #8A7A66)",
  },
};
